import { duration } from '@play-co/components';
import { State } from '../State';
import {
  WeeklyLeaderboardCommonEventProperties,
  WeeklyLeaderboardUserProperties,
} from './weeklyLeaderboard.analytics.schema';
import {
  WEEKLY_LEADERBOARD_INIT_DATE,
  weeklyLeaderboardSchedule,
  weeklyLeaderboardSize,
  weeklyLeaderboardUnlockLevel,
} from './weeklyLeaderboard.ruleset';
import { DateTime } from 'luxon';
import { isGuestUser } from '../guest/guest.getters';
import features from '../ruleset/features';
import { getBuruFriends } from '../friends/friends.getters';
import { AvatarFrameId } from '../profile/frames';
import {
  getPlayerProfile,
  getPlayerProfileAvatarFrame,
} from '../profile/profile.getters';
import { Locale } from '../ruleset/locale';

/**
 * Check if weeklyLeaderboards AB test is enabled for the player.
 * @param state
 * @returns True if enabled, false otherwise
 */
export function isWeeklyLeaderboardsEnabled(state: State): boolean {
  // Disable in guest mode.
  if (isGuestUser(state)) {
    return false;
  }

  return features(state).weeklyLeaderboards;
}

/**
 * Not available if the player hasn't reached the required level
 */
export function isWeeklyLeaderboardsUnlocked(state: State): boolean {
  return state.match2.level >= weeklyLeaderboardUnlockLevel;
}

/**
 * Check if weeklyLeaderboards are available for the player.
 * @param state
 * @param now
 * @returns True if available, false otherwise
 */
export function isWeeklyLeaderboardsAvailable(
  state: State,
  now: number,
): boolean {
  // Not available if the player hasn't reached the required level
  if (!isWeeklyLeaderboardsUnlocked(state)) {
    return false;
  }

  // Not available if the feature is disabled
  if (!isWeeklyLeaderboardsEnabled(state)) {
    return false;
  }

  // Not available if there is no schedule
  if (!weeklyLeaderboardSchedule.getCurrentEvent(now)) {
    return false;
  }

  // All checks passed, weeklyLeaderboards are available
  return true;
}

/**
 * Retrieve the ongoing weekly weeklyLeaderboards event.
 * @param state
 * @param now
 * @returns Ongoing event if available, undefined otherwise
 */
export function getOngoingWeeklyLeaderboardEvent(
  state: State,
  now: number,
):
  | {
      remainingTime: number;
      id: string;
      start: number;
      end: number;
      metadata?: undefined;
    }
  | undefined {
  if (!isWeeklyLeaderboardsAvailable(state, now)) {
    return undefined;
  }

  const schedule = weeklyLeaderboardSchedule.getCurrentEvent(now);
  if (!schedule) {
    return undefined;
  }

  return {
    ...schedule,
    remainingTime: weeklyLeaderboardSchedule.getRemainingTime(now),
  };
}

/**
 * Get the last weekly leaderboard entry that the user joined.
 * @param state
 * @param now
 * @returns The last weekly leaderboard that the player joined, or undefined if none
 */
export function getWeeklyLeaderboardLastEntry(
  state: State,
  now: number,
):
  | {
      readonly position?: number | undefined;
      readonly claimedAt?: number | undefined;
      readonly score: number;
      readonly joinedAt: number;
      readonly opponents: readonly string[];
    }
  | undefined {
  if (!state.weeklyLeaderboard) {
    return undefined;
  }

  const currentEvent = getOngoingWeeklyLeaderboardEvent(state, now);
  let lastId: string | undefined = undefined;

  for (const eventId of Object.keys(state.weeklyLeaderboard.entries)) {
    if (eventId === currentEvent?.id) {
      continue;
    }

    // Event id is a time string, so we can compare based on that
    const eventTime = DateTime.fromISO(eventId).toMillis();
    if (!lastId || eventTime > DateTime.fromISO(lastId).toMillis()) {
      lastId = eventId;
    }
  }

  // Return undefined if the player hasn't joined any event
  if (!lastId) return undefined;

  // Return the last weekly leaderboard that the player joined before the current event
  return state.weeklyLeaderboard.entries[lastId];
}

export type WeeklyLeaderboardRankingArgs = {
  eventId: string;
  opponents: string[];
  score: number;
  joinedAt: number;
  locale: Locale;
  ownProfile: { name: string; photo: string };
  ownFrame: AvatarFrameId;
};

export function getWeeklyLeaderboardRankingArgs(
  state: State,
  eventId: string,
): WeeklyLeaderboardRankingArgs | undefined {
  const entry = state.weeklyLeaderboard?.entries[eventId];
  if (!entry) return undefined;

  return {
    eventId,
    opponents: [...entry.opponents],
    score: entry.score,
    joinedAt: entry.joinedAt,
    locale: state.settings.locale as Locale,
    ownProfile: getPlayerProfile(
      state,
      state.publicProfile?.name,
      state.publicProfile?.photo,
      state.profile?.name,
      state.profile?.photo,
      true,
    ),
    ownFrame: getPlayerProfileAvatarFrame(state),
  };
}

/**
 * Retrieve a list of users sorted by joinedAt and score.
 * @param list
 * @returns
 */
export function sortWeeklyLeaderboardRanking<
  T extends { score: number; joinedAt: number },
>(list: T[]): T[] {
  const newList = list.slice();
  // Sort the list by joinedAt - oldest to join first
  newList.sort((a, b) => a.joinedAt - b.joinedAt);
  // Sort the list by score - highest score first
  newList.sort((a, b) => b.score - a.score);
  // Return the list already sorted by ranking
  return newList;
}

/**
 * Get the weekly leaderboard number of weeks (cycles) since the first event, starting from 1.
 * @param now
 * @returns
 */
export function getWeeklyLeaderboardCycle(now: number): number {
  // The initDateTime is the time of the first event, but not exposed in the schedule
  const start = DateTime.fromISO(WEEKLY_LEADERBOARD_INIT_DATE);
  const week = duration({ days: 7 });
  // Return how many weeks have passed since the start
  return Math.floor((now - start.toMillis()) / week) + 1;
}

/**
 * Return an extended list of friends IDs that includes the player's friends.
 * @param state
 * @returns
 */
export function getWeeklyLeaderboardFriendsIDs(state: State): string[] {
  return getBuruFriends(state);
}

/**
 * Get common event properties for the weekly leaderboard.
 * @param state
 * @param eventId
 * @param now
 * @returns
 */
export function getWeeklyLeaderboardCommonEventProperties(
  state: State,
  eventId: string,
  now: number,
): WeeklyLeaderboardCommonEventProperties {
  const userEntry = state.weeklyLeaderboard?.entries[eventId];
  const userFriends = getWeeklyLeaderboardFriendsIDs(state);
  const numFriends =
    userEntry?.opponents.filter((id) => userFriends.includes(id)).length ?? 0;

  return {
    id: eventId,
    cycle: getWeeklyLeaderboardCycle(now),
    friendsCount: numFriends,
    rank: userEntry?.position ?? 0,
    score: userEntry?.score ?? 0,
    isFull: userEntry?.opponents.length === weeklyLeaderboardSize - 1,
  };
}

/**
 * Retrieve the Super Lollipop user properties
 * @param state
 * @returns
 */
export function getWeeklyLeaderboardUserProperties(
  state: State,
  now: number,
): WeeklyLeaderboardUserProperties {
  // If the feature is not available, return an empty object
  if (!isWeeklyLeaderboardsAvailable(state, now)) {
    return {};
  }

  const event = getOngoingWeeklyLeaderboardEvent(state, now);
  const userEntry = state.weeklyLeaderboard?.entries[event?.id ?? ''];

  return {
    lastExitWeeklyLeaderboardId: event?.id,
    lastExitWeeklyLeaderboardRank: userEntry?.position,
    lastExitWeeklyLeaderboardScore: userEntry?.score,
  };
}
